// Claymation Ad's script as its form reads it (spec G14-claymation-ad, PR d1; live's
// `claymation-ad-form/library-view`, read as a behaviour spec): the beats' labels, the said words
// and the meta line recounted on every edit, the shots' headers and the summary lines, and what
// keeps the script from approval. The server counts and checks the same on Approve script
// (`templates/claymation_script.py`: `count_said_words`, `script_issue`).
import type { ClaymationBeat, ClaymationScript, ClaymationShot } from "../../../api/generated/model";

/** Live's words (`library-view/constants.ts`). */
export const PICK_PRODUCT = "Pick a product and its packshot";
export const PICK_NARRATOR = "Pick the narrator voice";
export const GENERATE_SCRIPT = "Generate the script";
export const SCRIPT_SHAPE = "The script needs its lines and its eight shots — generate it again";
export const EMPTY_LINE = "Every line needs what is written and what is said";
export const OVER_BUDGET = "At most 76 words — shorten the script";
export const EMPTY_CLOSING_LINE = "Fill in the closing line";
export const EMPTY_BRAND_URL = "Fill in the brand URL";
export const EMPTY_HEADLINE = "Write the end card headline";
export const EMPTY_BUTTON = "Fill in the CTA: it is the end card button";
export const UNREADABLE_PACK =
  "The pack was hard to read — type the product name or its text and generate again.";

const BEAT_LABELS: Readonly<Record<ClaymationBeat, string>> = {
  hook: "hook",
  habit: "habit",
  fact_bridge: "fact bridge",
  contrast: "contrast",
  reveal: "reveal",
  product_a: "product",
  product_b: "product B",
  proof: "proof",
  close: "close",
};

/** The lines by product count, and the lines each of the eight shots carries (live's
 * `timing/structure.ts`): with two products, shot 1 carries lines 1 and 2. */
const LINES_BY_COUNT = { 1: 8, 2: 9 } as const;
const SHOT_LINES_BY_COUNT = {
  1: [[1], [2], [3], [4], [5], [6], [7], [8]],
  2: [[1, 2], [3], [4], [5], [6], [7], [8], [9]],
} as const;

/** Live's budget of said words (`WORD_BUDGET`), its narrator's pace in words a second, the hero
 * shot's tail and the end card's seconds. */
export const WORD_BUDGET = { min: 66, max: 76, line: 14 } as const;
const NARRATOR_PACE = 1.9;
const TAIL_S = 0.5;
const END_CARD_S = 4;

/** A beat as live labels it: the first of two products is "product A". */
export function beatLabel(script: ClaymationScript, beat: ClaymationBeat): string {
  return beat === "product_a" && script.product_count === 2 ? "product A" : BEAT_LABELS[beat];
}

const EDGE_MARKS = /^[?.,!"“”:;]+|[?.,!"“”:;]+$/g;
const LETTER_GROUP = /^[A-Z](?:-[A-Z])+$/;
const NUMBER = /^(\d+)(%?)$/;

/** How many words a number is said as: "seventy-two" one, "one hundred" two, from 10,000 one. */
function numberWords(value: number): number {
  if (value < 100 || value >= 10_000) return 1;
  if (value < 1_000) return 2 + (value % 100 === 0 ? 0 : numberWords(value % 100));
  const rest = value % 1_000;
  return numberWords(Math.floor(value / 1_000)) + 1 + (rest === 0 ? 0 : numberWords(rest));
}

function tokenWords(token: string): number {
  const bare = token.replace(EDGE_MARKS, "");
  if (bare === "") return 0;
  if (LETTER_GROUP.test(bare)) return Math.ceil(bare.replaceAll("-", "").length / 2);
  const number = NUMBER.exec(bare);
  if (number !== null) return numberWords(Number(number[1])) + (number[2] === "%" ? 1 : 0);
  return 1;
}

/** A line's words as the narrator says them: a hyphenated word one, "100%" three, "P-T-F-E" two. */
export function countSaidWords(text: string): number {
  return text.split(/\s+/).reduce((total, token) => total + tokenWords(token), 0);
}

export function scriptWords(script: ClaymationScript): number {
  return script.lines.reduce((total, line) => total + countSaidWords(line.text), 0);
}

/** The voiceover's seconds before any take, at live's narrator's pace. */
export function plannedVoiceSeconds(words: number): number {
  return words / NARRATOR_PACE;
}

/** Live's meta line: "8 lines · 8 shots · 70 / 66–76 words · voice ≈ 37 s · ad ≈ 42 s". */
export function scriptMeta(script: ClaymationScript): string {
  const words = scriptWords(script);
  const voice = plannedVoiceSeconds(words);
  const ad = Math.ceil(voice + TAIL_S) + END_CARD_S;
  return [
    `${script.lines.length} lines`,
    `${script.shots.length} shots`,
    `${words} / ${WORD_BUDGET.min}–${WORD_BUDGET.max} words`,
    `voice ≈ ${Math.round(voice)} s`,
    `ad ≈ ${ad} s`,
  ].join(" · ");
}

/** The chips a filled value of wins over the director's at approval. */
export interface ScriptChips {
  brand_url: string;
  closing_line: string;
  cta: string;
}

const filled = (chip: string, written: string) => chip.trim() !== "" || written.trim() !== "";

/** What keeps the script from approval, in live's order and words (`findScriptIssue`), its shape
 * checked as the server's (the line ids and shot ids in order, each shot's lines), or null. */
export function scriptIssue(script: ClaymationScript | null, chips: ScriptChips): string | null {
  if (script === null) return GENERATE_SCRIPT;
  const shotLines = SHOT_LINES_BY_COUNT[script.product_count];
  const shaped =
    script.lines.length === LINES_BY_COUNT[script.product_count] &&
    script.lines.every((line, at) => line.id === at + 1) &&
    script.shots.length === shotLines.length &&
    script.shots.every(
      (shot, at) => shot.shot_id === at + 1 && shot.line_ids.join() === (shotLines[at] ?? []).join(),
    );
  if (!shaped) return SCRIPT_SHAPE;
  if (script.lines.some((line) => line.text.trim() === "" || line.spoken.trim() === "")) return EMPTY_LINE;
  if (scriptWords(script) > WORD_BUDGET.max) return OVER_BUDGET;
  if (!filled(chips.closing_line, script.end_line)) return EMPTY_CLOSING_LINE;
  if (!filled(chips.brand_url, script.brand_url)) return EMPTY_BRAND_URL;
  if (script.end_card.headline.trim() === "") return EMPTY_HEADLINE;
  return filled(chips.cta, script.end_card.cta_button) ? null : EMPTY_BUTTON;
}

/** The shot line `index` opens, or null for a line inside a shot (shot 1 with two products). */
export function shotOpenedAt(script: ClaymationScript, index: number): ClaymationShot | null {
  const line = script.lines[index];
  return line === undefined ? null : (script.shots.find((shot) => shot.line_ids[0] === line.id) ?? null);
}

/** A shot's header (live's `toShotHeaderLabel`): "Shot 1 · S1 the kitchen · Mo · SLAB". */
export function shotHeader(script: ClaymationScript, shot: ClaymationShot): string {
  const place = script.sets.find((set) => set.id === shot.set_id)?.place ?? shot.set_id;
  const products = shot.products_in_frame.map(
    (id) => script.products.find((product) => product.id === id)?.name ?? id,
  );
  const inFrame = [...(shot.character_in_frame ? [script.character.name] : []), ...products];
  return [`Shot ${shot.shot_id}`, `${shot.set_id} ${place}`, ...inFrame].join(" · ");
}

/** The summary above the lines (live's `ScriptSummary`): a line a product, the cast and its
 * sets, and the mechanism. */
export function scriptSummary(script: ClaymationScript): string[] {
  const products = script.products.map(
    (product) => `${script.brand} ${product.name} — ${[product.made_from, product.job].join(" · ")}`,
  );
  const sets = script.sets.map((set) => `${set.id} ${set.place}`);
  const cast = [`${script.character.name}, ${script.character.who}`, ...sets].join(" · ");
  return [...products, cast, `Mechanism: ${script.mechanism}`];
}
